import { useState } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import { STD_MATERIALS, STD_THICKNESS, STD_COLOURS, MATERIAL_STATUS_META } from "@/shared/lib/materialOptions";
import { Package, Plus, Trash2, Send, Lock, CheckCircle2, Paperclip, Pencil, CornerDownRight } from "lucide-react";
import { toast } from "sonner";

/* Msg 895 — Designer material declaration (per approved BOQ line).
   Multiple items, each Standard (dropdowns) or Non-Standard (free text + source).
   A single line can therefore be Standard, Non-Standard, or Mixed. */

const emptyStd = () => ({
  kind: "standard", material: "", thickness: "", thickness_custom: "",
  colour: "", colour_custom: "", spec: "", source: "",
  quantity: "", unit: "Nos", remarks: "", attachment_url: "", attachment_name: "",
});

export default function MaterialDeclarationPanel({ scope, onDone }) {
  const status = scope.material_status || "pending";
  // Hard lock — Procurement has started, nothing can change.
  const hardLocked = status === "in_progress" || status === "procured";
  const seeded = (scope.material_items && scope.material_items.length)
    ? scope.material_items.map((m) => ({ ...emptyStd(), ...m, quantity: m.quantity ?? "" }))
    : [emptyStd()];
  const [items, setItems] = useState(seeded);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  // Soft lock — already submitted to Procurement; read-only until the designer
  // explicitly chooses to Update. (Msg 930)
  const softLocked = status === "submitted" && !editing;
  const readOnly = hardLocked || softLocked;
  const meta = MATERIAL_STATUS_META[status] || MATERIAL_STATUS_META.pending;

  function update(i, patch) {
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  }
  function addItem() { setItems((arr) => [...arr, emptyStd()]); }
  function removeItem(i) { setItems((arr) => (arr.length === 1 ? arr : arr.filter((_, idx) => idx !== i))); }
  function cancelEdit() { setItems(seeded); setEditing(false); }

  async function submit() {
    const payload = items.map((it) => ({
      kind: it.kind,
      material: it.material, thickness: it.thickness, thickness_custom: it.thickness_custom,
      colour: it.colour, colour_custom: it.colour_custom,
      spec: it.spec, source: it.source,
      quantity: parseFloat(it.quantity || "0") || 0, unit: it.unit || "Nos",
      remarks: it.remarks, attachment_url: it.attachment_url, attachment_name: it.attachment_name,
    }));
    setSaving(true);
    try {
      await api.post(`/my-designs/${scope.project_code}/${scope.boq_id}/material`, { items: payload });
      toast.success("Material requirements submitted to Procurement.");
      setEditing(false);
      onDone && onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Submit failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="px-5 sm:px-6 py-5 bg-indigo-50/40 border-t border-indigo-100" data-testid={`material-panel-${scope.boq_id}`}>
      {/* Nested sub-sub-section: this wall → its material requirements. The left
          accent rail signals it belongs to the wall above it. */}
      <div className="border-l-2 border-indigo-200 pl-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-indigo-700">
            <CornerDownRight className="w-3.5 h-3.5 text-indigo-300" />
            <Package className="w-3.5 h-3.5" /> Material requirements
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 h-6 rounded-full text-[10px] font-medium border ${meta.cls}`}
                data-testid={`material-status-${scope.boq_id}`}>
            {status === "procured" && <CheckCircle2 className="w-3 h-3" />}
            {hardLocked && status !== "procured" && <Lock className="w-3 h-3" />}
            {meta.label}
          </span>
        </div>

        {readOnly ? (
          <div className="space-y-2">
            {items.map((it, i) => <ReadOnlyItem key={i} it={it} idx={i} />)}
            {hardLocked ? (
              <div className="text-xs text-zinc-500 flex items-center gap-1.5 mt-2">
                <Lock className="w-3.5 h-3.5" /> Procurement has started — these details are locked.
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2 pt-2">
                <div className="text-xs text-emerald-600 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Submitted to Procurement.
                </div>
                <button onClick={() => setEditing(true)} data-testid={`material-update-${scope.boq_id}`}
                        className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-indigo-200 bg-white text-xs text-indigo-700 font-medium hover:bg-indigo-50 transition">
                  <Pencil className="w-3.5 h-3.5" /> Update
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it, i) => (
              <ItemEditor key={i} it={it} idx={i} boqId={scope.boq_id}
                          onChange={(p) => update(i, p)} onRemove={() => removeItem(i)}
                          canRemove={items.length > 1} />
            ))}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <button onClick={addItem} data-testid={`material-add-item-${scope.boq_id}`}
                      className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-indigo-200 bg-white text-sm text-indigo-700 hover:bg-indigo-50 transition">
                <Plus className="w-4 h-4" /> Add item
              </button>
              <button onClick={submit} disabled={saving} data-testid={`material-submit-${scope.boq_id}`}
                      className="inline-flex items-center gap-2 px-5 h-9 rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-medium shadow-sm hover:shadow-md transition disabled:opacity-50">
                <Send className="w-4 h-4" /> {saving ? "Submitting…" : (status === "submitted" ? "Update & resubmit" : "Submit to Procurement")}
              </button>
              {editing && (
                <button onClick={cancelEdit} data-testid={`material-cancel-${scope.boq_id}`}
                        className="px-3 h-9 text-sm text-zinc-500 hover:text-zinc-800 transition">Cancel</button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ItemEditor({ it, idx, boqId, onChange, onRemove, canRemove }) {
  const isStd = it.kind !== "non_standard";
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={`material-item-${boqId}-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden text-xs">
          <button onClick={() => onChange({ kind: "standard" })}
                  className={`px-3 h-8 font-medium transition ${isStd ? "bg-indigo-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid={`material-kind-std-${boqId}-${idx}`}>Standard</button>
          <button onClick={() => onChange({ kind: "non_standard" })}
                  className={`px-3 h-8 font-medium transition ${!isStd ? "bg-orange-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                  data-testid={`material-kind-nonstd-${boqId}-${idx}`}>Non-Standard</button>
        </div>
        {canRemove && (
          <button onClick={onRemove} className="text-zinc-400 hover:text-rose-600 transition" data-testid={`material-remove-${boqId}-${idx}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {isStd ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Picker label="Material" value={it.material} onChange={(v) => onChange({ material: v })}
                  options={STD_MATERIALS} placeholder="Select material" testid={`material-material-${boqId}-${idx}`} />
          <PickerWithCustom label="Thickness" value={it.thickness} custom={it.thickness_custom}
                            onChange={(v) => onChange({ thickness: v })} onCustom={(v) => onChange({ thickness_custom: v })}
                            options={STD_THICKNESS} testid={`material-thickness-${boqId}-${idx}`} />
          <PickerWithCustom label="Colour / Finish" value={it.colour} custom={it.colour_custom}
                            onChange={(v) => onChange({ colour: v })} onCustom={(v) => onChange({ colour_custom: v })}
                            options={STD_COLOURS} testid={`material-colour-${boqId}-${idx}`} />
        </div>
      ) : (
        <div className="space-y-3">
          <Field label="Material required / specification" required>
            <textarea rows={2} value={it.spec} onChange={(e) => onChange({ spec: e.target.value })}
                      placeholder="e.g. Imported brushed-brass sheet, 1.2mm"
                      className="w-full text-sm rounded-lg border border-zinc-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-300"
                      data-testid={`material-spec-${boqId}-${idx}`} />
          </Field>
          <Field label="Procurement source / where to buy from" required>
            <input value={it.source} onChange={(e) => onChange({ source: e.target.value })}
                   placeholder="e.g. Metro Metals, Mumbai (vendor / market / supplier)"
                   className="w-full h-10 text-sm rounded-lg border border-zinc-200 px-3 focus:outline-none focus:ring-2 focus:ring-orange-300"
                   data-testid={`material-source-${boqId}-${idx}`} />
          </Field>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
        <Field label="Quantity required" required>
          <div className="flex gap-2">
            <input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => onChange({ quantity: e.target.value })}
                   className="w-full h-10 text-sm rounded-lg border border-zinc-200 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                   data-testid={`material-qty-${boqId}-${idx}`} />
            <input value={it.unit} onChange={(e) => onChange({ unit: e.target.value })}
                   className="w-20 h-10 text-sm rounded-lg border border-zinc-200 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                   data-testid={`material-unit-${boqId}-${idx}`} />
          </div>
        </Field>
        <Field label="Remarks">
          <input value={it.remarks} onChange={(e) => onChange({ remarks: e.target.value })}
                 placeholder="Optional"
                 className="w-full h-10 text-sm rounded-lg border border-zinc-200 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                 data-testid={`material-remarks-${boqId}-${idx}`} />
        </Field>
        <Field label="Attachment / reference">
          <FileUpload value={it.attachment_url}
                      onChange={(u) => onChange({ attachment_url: u, attachment_name: u ? (u.split("/").pop() || "ref") : "" })}
                      label="Attach file" accept=".pdf,.png,.jpg,.jpeg,.webp,.xlsx,.docx"
                      testid={`material-file-${boqId}-${idx}`} />
        </Field>
      </div>
    </div>
  );
}

function ReadOnlyItem({ it, idx }) {
  const isStd = it.kind !== "non_standard";
  const name = isStd ? it.material : it.spec;
  const bits = isStd
    ? [it.thickness_custom || it.thickness, it.colour_custom || it.colour].filter(Boolean)
    : [it.source].filter(Boolean);
  return (
    <div className="flex items-center gap-2 text-sm rounded-lg bg-white border border-zinc-200 px-3 py-2">
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${isStd ? "bg-indigo-50 text-indigo-700" : "bg-orange-50 text-orange-700"}`}>
        {isStd ? "Std" : "Non-Std"}
      </span>
      <span className="font-medium text-zinc-800">{name || "—"}</span>
      {bits.length > 0 && <span className="text-zinc-400 text-xs">· {bits.join(" · ")}</span>}
      <span className="ml-auto text-xs text-zinc-500">{it.quantity} {it.unit}</span>
      {it.attachment_url && <a href={it.attachment_url} target="_blank" rel="noreferrer" className="text-indigo-500"><Paperclip className="w-3.5 h-3.5" /></a>}
    </div>
  );
}

function Picker({ label, value, onChange, options, placeholder, testid }) {
  return (
    <Field label={label} required>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full h-10 text-sm rounded-lg border border-zinc-200 px-3 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid={testid}>
        <option value="">{placeholder || "Select"}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

function PickerWithCustom({ label, value, custom, onChange, onCustom, options, testid }) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full h-10 text-sm rounded-lg border border-zinc-200 px-3 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
              data-testid={testid}>
        <option value="">Select</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      {value === "Custom" && (
        <input value={custom} onChange={(e) => onCustom(e.target.value)} placeholder="Enter custom value"
               className="w-full h-9 mt-2 text-sm rounded-lg border border-zinc-200 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
               data-testid={`${testid}-custom`} />
      )}
    </Field>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
        {label}{required && <span className="text-rose-400"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
